import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import {
  useWidgetContext,
  Translation,
  Loader,
  ShowIfTranslationExists,
  usePlatformConfig,
} from '@dxp-module/common'
import { tokenService } from '@dxp-module/login'
import { MyAccountTheme } from '@dxp-module/themes'
import { Box, Button, Typography, useMediaQuery, useTheme } from '@mui/material'
import PersonIcon from '@mui/icons-material/Person'
import miniWidgetButtonStyles from '../widgetButton/miniWidgetButton.styles'
import useGetMyInformation from '../../../myAccount/hooks/useGetMyInformation'
import useCustomerInformation from '../../../myAccount/hooks/useCustomerInformation'
import MiniWidgetDropdownButton from './MiniWidgetDropdownButton'

type MiniWidgetButtonProps = {
  toggleModal?: (newValue: boolean) => void
}

const MiniWidgetButton: FunctionComponent<MiniWidgetButtonProps> = ({
  toggleModal,
}) => {
  const {
    platformConfig: { myAccount },
  } = usePlatformConfig()
  const { loggedOutOptions } =
    myAccount?.miniMyAccountConfig?.dropdownOptions ?? {}

  const { loading, refetch } = useGetMyInformation()
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(
    !!tokenService.getCurrentToken()
  )
  const {
    configuration: { loginInModal, myAccountUrl },
  } = useWidgetContext()

  const styles = miniWidgetButtonStyles({})
  const { breakpoints } = useTheme<MyAccountTheme>()
  const isMobile = useMediaQuery(breakpoints.down('sm'))
  const [{ firstName, lastName }] = useCustomerInformation()
  const intl = useIntl()
  const welcomeMessage = intl.formatMessage(
    {
      id: 'trans__myaccount__mini_widget__welcome_firstname_label',
    },
    {
      firstname: firstName,
      lastname: lastName,
    },
    {
      ignoreTag: true,
    }
  )
  const translationId = loginInModal
    ? 'trans__myaccount__create_an_account__existing_customer_label'
    : 'trans__myaccount__mini_widget__login_CTA'

  const handleOnClick = useCallback(() => {
    if (loginInModal) {
      return toggleModal && toggleModal(true)
    }

    return (window.location.href = myAccountUrl ?? '')
  }, [loginInModal, myAccountUrl])

  useEffect(() => {
    const intervalId = setInterval(() => {
      const token = tokenService.getCurrentToken()
      setIsLoggedIn(!!token)
    }, 3000)

    return () => {
      clearInterval(intervalId)
    }
  }, [])

  useEffect(() => {
    if (isLoggedIn && (!firstName || !lastName)) {
      refetch()
    }
  }, [isLoggedIn, firstName, lastName])

  if (loading) {
    return <Loader />
  }

  if ((isMobile || !!loggedOutOptions) && !loginInModal) {
    return (
      <MiniWidgetDropdownButton
        welcomeMessage={welcomeMessage}
        isLoggedIn={isLoggedIn}
      />
    )
  }

  if (isLoggedIn) {
    if (firstName) {
      return (
        <ShowIfTranslationExists
          translationKey={
            'trans__myaccount__mini_widget__welcome_firstname_label'
          }
        >
          <Box
            className={styles.container}
            data-test="mini:myaccount:container:signed:in"
          >
            <Typography
              className={styles.welcomeMessage}
              data-test="mini:myaccount:dropdown:welcome"
              component="span"
              dangerouslySetInnerHTML={{
                __html: welcomeMessage,
              }}
            />
            <MiniWidgetDropdownButton
              welcomeMessage={welcomeMessage}
              isLoggedIn={isLoggedIn}
            />
          </Box>
        </ShowIfTranslationExists>
      )
    }
  }

  return (
    <Button
      className={loginInModal ? styles.buttonText : styles.root}
      onClick={handleOnClick}
    >
      <PersonIcon className={styles.icon} />
      <Translation id={translationId} />
    </Button>
  )
}

export default MiniWidgetButton
